"use client";

import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

export type InputSize = "sm" | "md" | "lg";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  inputSize?: InputSize;
  error?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
}

const sizeClasses: Record<InputSize, string> = {
  sm: "h-7 px-2.5 text-[12px]",
  md: "h-8 px-3 text-[13px]",
  lg: "h-10 px-3.5 text-sm",
};

// GAM Brand v2: input radius + spring from active skin tokens.
// See docs/BRAND.md Layer 2.
const inputGamStyle = {
  borderRadius: "var(--gam-radius-input, var(--cc-radius, 0.5rem))",
  transitionTimingFunction: "var(--gam-spring-default, cubic-bezier(0.4, 0, 0.2, 1))",
} as const;

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ inputSize = "md", error, iconStart, iconEnd, className = "", style, ...rest }, ref) => {
    const hasStart = !!iconStart;
    const hasEnd = !!iconEnd;

    return (
      <div className="relative">
        {hasStart && (
          <span className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-2.5 text-slate-500">
            {iconStart}
          </span>
        )}
        <input
          ref={ref}
          style={{ ...inputGamStyle, ...(style || {}) }}
          className={[
            "w-full border bg-[var(--theme-surface)] text-[var(--theme-text)] outline-none transition-colors",
            "placeholder:text-[var(--theme-text-faint)]",
            "focus:bg-[var(--theme-surface-hover)]",
            "disabled:pointer-events-none disabled:opacity-50",
            error
              ? "border-[var(--role-error)] focus-visible:border-[var(--role-error)]"
              : "border-[var(--theme-border)] focus-visible:border-[var(--theme-primary)]",
            sizeClasses[inputSize],
            hasStart ? "ps-8" : "",
            hasEnd ? "pe-8" : "",
            className,
          ].join(" ")}
          {...rest}
        />
        {hasEnd && (
          <span className="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-2.5 text-slate-500">
            {iconEnd}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
export { Input };
